import { ReactNode, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Container,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  useTheme,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  HEADER_HEIGHT,
  PROFILE_SUBNAV_HEIGHT,
  USER_QUALIFY_STATUS,
  USER_ROLE,
  Z_INDEX,
} from '@/config/constants';
import { isLoanFlowPath } from '@/config/loanNavPaths';
import { useGetAllContractsQuery } from '@/apis/extended/agentApi';
import { useGetMyBuyerContractsQuery } from '@/apis/extended/contractApi';
import { useGetMyLoanApplicationsQuery } from '@/apis/extended/loanApplicationApi';
import { useGetProductsQuery } from '@/apis/extended/productApi';
import { useGetAllSellerContractsQuery } from '@/apis/extended/sellerAPI';
import { SigninModal } from '@/components/Auth/SigninModal';
import { SignOutModal } from '@/components/Auth/SignOutModal';
import { SignUpModal } from '@/components/Auth/SignUpModal';
import { ModeIndicator } from '@/components/Common/ModeIndicator';
import { BuyingPowerChip } from '@/components/Layout/App/BuyingPowerChip';
import { LoanNavTabs } from '@/components/Layout/App/LoanNavTabs';
import { Logo } from '@/components/Logo/Logo';
import { UserActionButton } from '@/components/UserAction/UserActionButton';
import { useAppLayout } from '@/hooks/useAppLayout';
import { useAuth } from '@/hooks/useAuth';
import { useDevice } from '@/hooks/useDevice';
import {
  buyerDashboardUrl,
  isNegotiationContractStatus,
  propertyNeedsHeaderDashboard,
  sellerDashboardUrl,
} from '@/utils/headerDashboard';
import { pickApplicationFor } from '@/utils/loanApplicationPick';
import { buildPropertyView } from '@/utils/propertyView/buildPropertyView';
import { normalizeSellerContractRow } from '@/utils/sellerContract/normalizeSellerProduct';
import type { SellerContractRecord } from '@/types/SellerContractType';

const NavIconButton = ({
  url,
  children,
}: {
  url: string;
  children: ReactNode;
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = location.pathname === url;

  return (
    <IconButton
      onClick={() => navigate(url)}
      sx={{
        color: isActive
          ? theme.palette.mainColors.primary
          : theme.palette.greyShades.grey1,
        position: 'relative',
        '&::after': {
          content: isActive ? '""' : 'none',
          position: 'absolute',
          left: 0,
          bottom: '0px',
          width: '100%',
          height: '2px',
          backgroundColor: theme.palette.mainColors.primary,
        },
      }}
    >
      {children}
    </IconButton>
  );
};

const NavTextButton = ({
  url,
  label,
  matchPrefix,
}: {
  url: string;
  label: string;
  matchPrefix?: string;
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = matchPrefix
    ? location.pathname === matchPrefix ||
      location.pathname.startsWith(`${matchPrefix}/`)
    : location.pathname === url;
  return (
    <Button
      variant="text"
      size="small"
      onClick={() => navigate(url)}
      sx={{
        color: isActive
          ? theme.palette.mainColors.primary
          : theme.palette.greyShades.grey1,
        fontWeight: 600,
        textTransform: 'none',
        position: 'relative',
        '&::after': {
          content: isActive ? '""' : 'none',
          position: 'absolute',
          left: 8,
          right: 8,
          bottom: 0,
          height: '2px',
          backgroundColor: theme.palette.mainColors.primary,
        },
      }}
    >
      {label}
    </Button>
  );
};

export const AppHeader = () => {
  const theme = useTheme();
  const { isAuthenticated, isAdmin, user } = useAuth();
  const { headerHeight, showSecondarySubnav } = useAppLayout();
  const { isMobile } = useDevice();
  const navigate = useNavigate();
  const location = useLocation();

  /** Blog, Services, About, and individual posts: same marketing nav as home (logged-out). */
  const marketingNavPaths = new Set([
    '/',
    '/blog',
    '/services',
    '/about-unbeatable',
    '/licensing',
  ]);
  const showMarketingNavLinks =
    !isAuthenticated &&
    (marketingNavPaths.has(location.pathname) ||
      location.pathname.startsWith('/post/'));

  const isBuyerLike =
    user?.role === USER_ROLE.BUYER || user?.role === USER_ROLE.USER;
  const isSeller = user?.role === USER_ROLE.SELLER;
  const isAgent = user?.role === USER_ROLE.SELLER_AGENT;

  const [dashboardAnchorEl, setDashboardAnchorEl] =
    useState<null | HTMLElement>(null);

  const { data: buyerContracts } = useGetMyBuyerContractsQuery(undefined, {
    skip: !isAuthenticated || !isBuyerLike,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    pollingInterval: 0,
  });
  const { data: buyerProductsData } = useGetProductsQuery(
    {},
    {
      skip: !isAuthenticated || !isBuyerLike,
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      pollingInterval: 0,
    }
  );
  const activeBuyerProductIds = useMemo(() => {
    return new Set(
      (buyerProductsData?.products ?? []).map((p) => String(p._id))
    );
  }, [buyerProductsData?.products]);
  const { data: sellerContracts } = useGetAllSellerContractsQuery(
    user?._id ?? '',
    {
      skip: !isAuthenticated || !isSeller || !user?._id,
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      pollingInterval: 0,
    }
  );
  const { data: agentContracts } = useGetAllContractsQuery(user?._id ?? '', {
    skip: !isAuthenticated || !isAgent || !user?._id,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    pollingInterval: 0,
  });

  const { data: loanApplications } = useGetMyLoanApplicationsQuery(undefined, {
    skip: !isAuthenticated || !isBuyerLike,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    pollingInterval: 0,
  });

  const buyerPropertyByProductId = useMemo(() => {
    if (!isBuyerLike)
      return new Map<string, ReturnType<typeof buildPropertyView>>();
    const products = buyerProductsData?.products ?? [];
    const apps = loanApplications ?? [];
    const map = new Map<string, ReturnType<typeof buildPropertyView>>();
    for (const product of products) {
      map.set(
        String(product._id),
        buildPropertyView({
          product,
          application: pickApplicationFor(product, apps),
          user,
        })
      );
    }
    return map;
  }, [isBuyerLike, buyerProductsData?.products, loanApplications, user]);

  const sellerPropertyByProductId = useMemo(() => {
    if (!isSeller)
      return new Map<string, ReturnType<typeof buildPropertyView>>();
    const map = new Map<string, ReturnType<typeof buildPropertyView>>();
    for (const row of (sellerContracts ??
      []) as unknown as SellerContractRecord[]) {
      const { product, contract, contractId } = normalizeSellerContractRow(row);
      map.set(
        product._id,
        buildPropertyView({
          product,
          contract,
          pipelineMode: 'seller',
          contractIdOverride: contractId,
        })
      );
    }
    return map;
  }, [isSeller, sellerContracts]);

  const getRoleDashboardUrl = (
    contractId: string,
    productId?: string,
    property?: ReturnType<typeof buildPropertyView>
  ) => {
    if (isBuyerLike && productId) {
      return buyerDashboardUrl(productId, property);
    }
    if (isSeller && productId) {
      return sellerDashboardUrl(productId, property);
    }
    if (isBuyerLike) return `/buyer/property-dashboard/${contractId}`;
    if (isSeller) return `/seller/property-dashboard/${contractId}`;
    if (isAgent) return `/agent/property-dashboard/${contractId}`;
    return `/view-contract/${contractId}`;
  };

  const formatAddress = (addr: any): string => {
    if (!addr) return '';
    if (typeof addr === 'string') return addr;
    return (
      addr.fullAddress ||
      [addr.street, addr.city, addr.state, addr.zip].filter(Boolean).join(', ')
    );
  };

  const formatStatus = (status?: string) => {
    if (!status) return 'Unknown';
    return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const getContractSummary = (item: any) => {
    const c = item?.contractDetails || item; // seller API sometimes nests under contractDetails
    const contractId = (c?._id?.toString?.() ||
      c?._id ||
      item?._id ||
      '') as string;
    const status = (c?.contractStatus || item?.contractStatus) as
      | string
      | undefined;
    const isDeleted = Boolean(c?.isDeleted || item?.isDeleted);
    const isVoided = status === 'voided';
    const ts =
      c?.ratifiedAt ||
      c?.updatedAt ||
      c?.createdAt ||
      item?.ratifiedAt ||
      item?.updatedAt ||
      item?.createdAt;

    const address =
      formatAddress(c?.product?.address) ||
      formatAddress(item?.propertyDetails?.address) ||
      '';
    const label =
      address ||
      (typeof c?.product?.title === 'string' ? c.product.title : '') ||
      'Property';
    const propertyStatus = formatStatus(status);
    const isActive = !isDeleted && !isVoided && !Boolean(c?.product?.isDeleted);
    const productId = (item?.propertyId?.toString?.() ||
      (typeof item?.propertyId === 'string' ? item.propertyId : '') ||
      c?.product?._id?.toString?.() ||
      (typeof c?.product === 'string' ? c.product : '') ||
      '') as string;

    return {
      contractId,
      productId,
      label,
      propertyStatus,
      isActive,
      timestamp: ts,
    };
  };

  const activeDashboards = (() => {
    const list =
      (isBuyerLike
        ? buyerContracts
        : isSeller
        ? sellerContracts
        : isAgent
        ? agentContracts
        : []) || [];

    const active = (list as any[]).map(getContractSummary).filter((x) => {
      if (!x.isActive || !x.contractId) return false;
      if (isSeller) return Boolean(x.productId);
      if (!isBuyerLike) return true;
      if (!x.productId) return false;
      return activeBuyerProductIds.has(x.productId);
    });

    active.sort((a, b) => {
      const aDate = new Date(a.timestamp || 0).getTime();
      const bDate = new Date(b.timestamp || 0).getTime();
      return bDate - aDate;
    });

    return active.map((x) => {
      const property = isBuyerLike
        ? buyerPropertyByProductId.get(x.productId)
        : isSeller
        ? sellerPropertyByProductId.get(x.productId)
        : undefined;
      return {
        contractId: x.contractId,
        productId: x.productId,
        label: x.label,
        status: x.propertyStatus,
        url: getRoleDashboardUrl(x.contractId, x.productId, property),
      };
    });
  })();

  const actionableDashboards = useMemo(() => {
    return activeDashboards.filter((d) => {
      if (isBuyerLike) {
        const property = buyerPropertyByProductId.get(d.productId);
        if (!property) return false;
        return propertyNeedsHeaderDashboard(property, d.status);
      }
      if (isSeller) {
        const property = sellerPropertyByProductId.get(d.productId);
        if (property) return propertyNeedsHeaderDashboard(property, d.status);
      }
      return isNegotiationContractStatus(d.status);
    });
  }, [
    activeDashboards,
    isBuyerLike,
    isSeller,
    buyerPropertyByProductId,
    sellerPropertyByProductId,
  ]);

  const dashboardUrl =
    actionableDashboards.length === 1 ? actionableDashboards[0].url : null;

  const openDashboardMenu = (e: React.MouseEvent<HTMLElement>) => {
    setDashboardAnchorEl(e.currentTarget);
  };
  const closeDashboardMenu = () => setDashboardAnchorEl(null);

  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const [isSignOutModalOpen, setIsSignOutModalOpen] = useState(false);

  const openSigninModal = () => {
    setIsSignInModalOpen(true);
  };

  const openSignUpModal = () => {
    setIsSignUpModalOpen(true);
  };

  return (
    <Box
      sx={{
        backgroundColor: 'var(--header-glass)',
        backdropFilter: 'blur(14px) saturate(1.2)',
        WebkitBackdropFilter: 'blur(14px) saturate(1.2)',
        borderBottom:
          '1px solid color-mix(in srgb, var(--outline-variant) 50%, transparent)',
        boxShadow: '0 8px 28px rgba(0, 8, 30, 0.06)',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: Z_INDEX.HEADER,
      }}
    >
      <Container
        sx={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '16px',
          height: `${HEADER_HEIGHT}px`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: '12px', md: '36px' },
          }}
        >
          <Logo />
          {!isMobile && showMarketingNavLinks && (
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gap: 0.25,
                flexWrap: 'wrap',
              }}
            >
              <Button
                variant="text"
                size="small"
                onClick={() => navigate('/blog')}
                sx={{
                  color: 'text.secondary',
                  fontWeight: 600,
                  textTransform: 'none',
                }}
              >
                Blog
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={() => navigate('/services')}
                sx={{
                  color: 'text.secondary',
                  fontWeight: 600,
                  textTransform: 'none',
                }}
              >
                Services
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={() => navigate('/about-unbeatable')}
                sx={{
                  color: 'text.secondary',
                  fontWeight: 600,
                  textTransform: 'none',
                }}
              >
                About
              </Button>
            </Box>
          )}
          {isAuthenticated &&
            (user?.role === USER_ROLE.BUYER ||
              user?.role === USER_ROLE.USER) && (
              <Box
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  gap: { xs: '4px', md: '12px' },
                }}
              >
                <NavTextButton
                  url="/properties"
                  label="Properties"
                  matchPrefix="/properties"
                />
                {user?.qualifyStatus === USER_QUALIFY_STATUS.QUALIFY && (
                  <NavTextButton url="/loan" label="Loan" matchPrefix="/loan" />
                )}
                <NavTextButton
                  url="/documents"
                  label="Documents"
                  matchPrefix="/documents"
                />
              </Box>
            )}
          {isAuthenticated && user?.role === USER_ROLE.SELLER_AGENT && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <NavIconButton url={`/agent/qualified-contract/${user?._id}`}>
                <i className="fa-light fa-file-contract" />
              </NavIconButton>
              <NavIconButton url={`/agent/dashboard/${user?._id}`}>
                <i className="fa-light fa-dumpster" />
              </NavIconButton>
            </div>
          )}
          {isAuthenticated && user?.role === USER_ROLE.SELLER && (
            <>
              <Box
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  gap: { xs: '4px', md: '12px' },
                }}
              >
                <NavTextButton
                  url="/properties"
                  label="Properties"
                  matchPrefix="/properties"
                />
              </Box>
              <NavIconButton url={`/seller/main/${user?._id}`}>
                <i className="fa-light fa-file-contract" />
              </NavIconButton>
              <NavIconButton url={`/seller/dashboard/${user?._id}`}>
                <i className="fa-light fa-dumpster" />
              </NavIconButton>
            </>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {isAuthenticated ? (
            <>
              {dashboardUrl && actionableDashboards.length === 1 && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => navigate(dashboardUrl)}
                  sx={{ mr: '4px' }}
                  startIcon={<i className="fa-light fa-grid-2" />}
                >
                  Dashboard
                </Button>
              )}
              {actionableDashboards.length > 1 && (
                <>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={openDashboardMenu}
                    sx={{ mr: '4px' }}
                    startIcon={<i className="fa-light fa-grid-2" />}
                    endIcon={<i className="fa-light fa-chevron-down" />}
                  >
                    Dashboard
                  </Button>
                  <Menu
                    anchorEl={dashboardAnchorEl}
                    open={Boolean(dashboardAnchorEl)}
                    onClose={closeDashboardMenu}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    {actionableDashboards.map((d) => (
                      <MenuItem
                        key={d.contractId}
                        onClick={() => {
                          closeDashboardMenu();
                          navigate(d.url);
                        }}
                        sx={{ maxWidth: 420 }}
                      >
                        <ListItemText
                          primary={d.label}
                          secondary={`${d.status} • ${String(
                            d.contractId
                          ).slice(-6)}`}
                        />
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
              {!isMobile && <UserActionButton />}
              <ModeIndicator />
              {isAdmin && (
                <NavIconButton url="/admin">
                  <i className="fa-light fa-shield-halved" />
                </NavIconButton>
              )}
              {isBuyerLike && !isMobile && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    pl: 0.75,
                    ml: 0.25,
                    flexShrink: 0,
                    borderLeft: `1px solid ${theme.palette.greyShades.border1}`,
                  }}
                >
                  <BuyingPowerChip />
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => navigate('/profile/settings')}
                    sx={{
                      fontWeight: 600,
                      textTransform: 'none',
                      color: location.pathname.startsWith('/profile')
                        ? theme.palette.mainColors.primary
                        : theme.palette.greyShades.grey1,
                    }}
                  >
                    Profile
                  </Button>
                </Box>
              )}
              {!isBuyerLike && (
                <NavIconButton url="/profile/settings">
                  <i className="fa-light fa-user" />
                </NavIconButton>
              )}
              <IconButton
                sx={{ color: theme.palette.greyShades.grey1 }}
                onClick={() => setIsSignOutModalOpen(true)}
              >
                <i className="fa-light fa-arrow-right-from-bracket" />
              </IconButton>
            </>
          ) : (
            <>
              <Button
                variant="text"
                onClick={openSigninModal}
                sx={{ color: 'text.primary' }}
              >
                Sign In
              </Button>
              <Button variant="contained" onClick={openSignUpModal}>
                Get Started
              </Button>
            </>
          )}
        </Box>
      </Container>
      {isAuthenticated && isLoanFlowPath(location.pathname) && <LoanNavTabs />}
      <Container
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          height: `${
            headerHeight -
            HEADER_HEIGHT -
            (showSecondarySubnav ? PROFILE_SUBNAV_HEIGHT : 0)
          }px`,
        }}
      >
        {isMobile && <UserActionButton sx={{ width: '100%' }} />}
      </Container>
      <SigninModal
        isOpen={isSignInModalOpen}
        onClose={() => setIsSignInModalOpen(false)}
      />
      <SignUpModal
        isOpen={isSignUpModalOpen}
        onClose={() => setIsSignUpModalOpen(false)}
      />
      <SignOutModal
        isOpen={isSignOutModalOpen}
        onClose={() => setIsSignOutModalOpen(false)}
      />
    </Box>
  );
};
